/**
 * Decorator for Enum.
 * @param {string} idPropertyName - property name to find enum value by property value. Usage in valueOf method
 * @return constructor of enum type
 */
export function Enum<T = any>(idPropertyName?: keyof T) {
    // tslint:disable-next-line
    return function <T extends (Function & EnumClass)>(target: T): T {
        const store: EnumStore = {
            name: target.prototype.constructor.name,
            enumMap: {},
            enumMapByName: {},
            enumValues: [],
            idPropName: idPropertyName
        };
        // Lookup static fields
        for (const fieldName of Object.keys(target)) {
            const value: any = (target as any)[fieldName];
            // Check static field: to be instance of enum type
            if (value instanceof target) {
                // O cast e exigido a partir do TypeScript 5.1 (hop 16). O `instanceof`
                // passou a estreitar o operando esquerdo para o tipo do `prototype` do
                // direito; como `target` e `Function & EnumClass`, o `value` -- que era
                // `any` -- passa a ser `{}` dentro deste ramo, e `{}` nao satisfaz
                // `Enumerable`. Em runtime nada muda: a guarda `instanceof` acima ja
                // garante que e mesmo uma instancia do enum.
                const enumItem: Enumerable = value as Enumerable;
                let id = fieldName;
                if (idPropertyName) {
                    id = (value as any)[idPropertyName];
                    if (typeof id !== 'string' && typeof id !== 'number') {
                        const enumName = store.name;
                        // String(...) exigido a partir do TypeScript 4.8 (hop 14): `idPropertyName`
                        // e `keyof T` com T = any, logo `string | number | symbol`, e a conversao
                        // implicita de symbol para string em template literal passou a ser erro.
                        throw new Error(`The value of the ${String(idPropertyName)} property in the enumeration element ${enumName}.${fieldName} is not a string or a number: ${id}`);
                    }
                }
                if (store.enumMap[id]) {
                    const enumName = store.name;
                    throw new Error(`An element with the identifier ${id}: ${enumName}.${store.enumMap[id].enumName} already exists in the enumeration ${enumName}`);
                }
                store.enumMap[id] = enumItem;
                store.enumMapByName[fieldName] = enumItem;
                store.enumValues.push(enumItem);
                enumItem.__enumName__ = fieldName;
                Object.freeze(enumItem);
            }
        }
        target.__store__ = store;
        Object.freeze(target.__store__);
        Object.freeze(target);
        return target;
    };
}

/** Key->Value type */
export interface EnumMap {
    [key: string]: Enumerable;
}

/** Type for Meta-Data of Enum */
export interface EnumClass {
    __store__: EnumStore;
}

/** Store Type. Keep meta data for enum */
export interface EnumStore {
    name: string;
    enumMap: EnumMap;
    enumMapByName: EnumMap;
    enumValues: Enumerable[];
    idPropName?: any;
}

/** Enum Item Type */
export interface EnumItemType {
    __enumName__: string;
}

/** Interface for IDE: autocomplete syntax and keywords */
export interface IStaticEnum<T> extends EnumClass {

    new(): { enumName: string };

    values(): ReadonlyArray<T>;

    valueOf(id: string | number): T;

    valueByName(name: string): T;
}

/** Base class for enum type */
export class Enumerable implements EnumItemType {
    // tslint:disable:variable-name
    // stub. need for type safety
    public static readonly __store__ = {} as EnumStore;
    // Initialize inside @Enum decorator
    public __enumName__ = '';

    // tslint:enable:variable-name

    public constructor() {
    }

    /** Get enum name */
    public get enumName(): string {
        return this.__enumName__;
    }

    private get topClass(): EnumClass {
        return this.constructor as any;
    }

    /**
     * Get all elements of enum
     * @return {ReadonlyArray<T>} all elements of enum
     */
    public static values(): ReadonlyArray<any> {
        return this.__store__.enumValues;
    }

    /**
     * Lookup enum item by id
     * @param {string | number} id - value for lookup
     * @return enum item by id
     */
    public static valueOf(id: string | number): any {
        const value = this.__store__.enumMap[id];
        if (!value) {
            throw new Error(`The element with ${id} identifier does not exist in the $ {clazz.name} enumeration`);
        }
        return value;
    }

    /**
     * Lookup enum item by enum name
     * @param {string} name - enum name
     * @return item by enum name
     */
    public static valueByName(name: string): any {
        const value = this.__store__.enumMapByName[name];
        if (!value) {
            throw new Error(`The element with ${name} name does not exist in the ${this.__store__.name} enumeration`);
        }
        return value;
    }

    /** Get enum id value or enum name */
    public toString(): string {
        const clazz = this.topClass;
        if (clazz.__store__.idPropName) {
            const self = this as any;
            return self[clazz.__store__.idPropName];
        }
        return this.enumName;
    }
}

/** 'Casting' method to make correct Enum Type */
export function EnumType<T>(): IStaticEnum<T> {
    return (Enumerable as IStaticEnum<T>);
}
